import { useEffect } from 'react';
import { Card, Row, Col, Statistic, Empty, Tabs, Tag } from 'antd';
import { AppstoreOutlined, BarChartOutlined, FileOutlined, UserOutlined, DashboardOutlined, FolderOutlined, PieChartOutlined, ThunderboltOutlined } from '@ant-design/icons';

import { useAuthStore } from '@/store/authStore';
import { useConfigStore } from '@/store/configStore';
import UploadBoard from '@/components/packages/UploadBoard';
import UploadStatsDashboard from '@/components/packages/UploadStatsDashboard';
import BcrDashboard from '@/components/bcr/BcrDashboard';
import FlowbackDashboard from '@/components/flowback/FlowbackDashboard';
import type { Project } from '@/types';

const ProjectCardGrid = ({ projects }: { projects: Project[] }) => (
  <Row gutter={[16, 16]}>
    {projects.map((project) => (
      <Col xs={24} sm={12} md={8} lg={6} key={project.id}>
        <Card
          hoverable
          className="text-center"
          onClick={() => (window.location.href = `/packages/${project.id}`)}
        >
          <div className="text-lg font-medium">{project.name}</div>
          <div className="text-gray-400 text-sm">{project.code}</div>
        </Card>
      </Col>
    ))}
  </Row>
);

const ProjectOverview = () => {
  const { user } = useAuthStore();
  const { projects, mountains, fetchProjects, fetchMountains } = useConfigStore();

  useEffect(() => {
    fetchProjects();
    fetchMountains();
  }, [fetchProjects, fetchMountains]);

  // 统一使用 configStore.projects（后端按用户权限过滤 + BVP 山头已注入）
  // 外部用户 permitted_projects 不含 mountain 字段，会导致 BVP-linked 项目显示为"未分组"
  const visibleProjects: Project[] = projects;

  if (visibleProjects.length === 0 && user?.user_type === 'external') {
    return (
      <div className="flex items-center justify-center h-96">
        <Empty description="暂无可访问的项目，请联系管理员配置权限" />
      </div>
    );
  }

  // 直接从项目数据构建山头分组，不依赖 BSP mountains store（BVP 山头可能不在 BSP 列表里）
  const mountainGroups = (() => {
    const seen = new Map<string, { name: string; sortOrder: number }>();
    for (const p of visibleProjects) {
      if (p.mountain?.name && !seen.has(p.mountain.name)) {
        const bspMountain = mountains.find((m) => m.name === p.mountain!.name);
        seen.set(p.mountain.name, {
          name: p.mountain.name,
          sortOrder: bspMountain?.sort_order ?? 999,
        });
      }
    }
    return Array.from(seen.values()).sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)
    );
  })();
  const ungroupedProjects = visibleProjects.filter((p) => !p.mountain);

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="可访问项目"
              value={visibleProjects.length}
              prefix={<AppstoreOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="用户类型"
              value={user?.user_type === 'internal' ? '内部用户' : '外部用户'}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="角色"
              value={user?.is_admin ? '管理员' : '普通用户'}
              prefix={<FileOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Mountain-grouped project cards */}
      {mountainGroups.map((mountain) => {
        const mProjects = visibleProjects.filter((p) => p.mountain?.name === mountain.name);
        if (mProjects.length === 0) return null;
        return (
          <Card
            key={mountain.name}
            className="mt-6"
            title={
              <span>
                <FolderOutlined className="mr-2 text-blue-500" />
                {mountain.name}
                <Tag className="ml-2" color="blue">{mProjects.length} 个项目</Tag>
              </span>
            }
          >
            <ProjectCardGrid projects={mProjects} />
          </Card>
        );
      })}

      {/* 未分组项目 */}
      {ungroupedProjects.length > 0 && (
        <Card className="mt-6" title="未分组">
          <ProjectCardGrid projects={ungroupedProjects} />
        </Card>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuthStore();

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">欢迎, {user?.display_name || user?.username}</h1>

      <Tabs
        defaultActiveKey="projects"
        items={[
          {
            key: 'projects',
            label: (
              <span>
                <AppstoreOutlined />
                项目总览
              </span>
            ),
            children: <ProjectOverview />,
          },
          ...(user?.user_type !== 'external'
            ? [
                {
                  key: 'upload-board',
                  label: (
                    <span>
                      <DashboardOutlined />
                      交付物上传列表
                    </span>
                  ),
                  children: <UploadBoard />,
                },
                {
                  key: 'upload-stats',
                  label: (
                    <span>
                      <PieChartOutlined />
                      交付物上传看板
                    </span>
                  ),
                  children: <UploadStatsDashboard />,
                },
                {
                  key: 'bcr-dashboard',
                  label: (
                    <span>
                      <BarChartOutlined />
                      BCR看板
                    </span>
                  ),
                  children: <BcrDashboard />,
                },
                {
                  key: 'flowback-dashboard',
                  label: (
                    <span>
                      <ThunderboltOutlined />
                      需求回流看板
                    </span>
                  ),
                  children: <FlowbackDashboard />,
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};

export default Dashboard;
